import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/SEOHead";

type State = "loading" | "ready" | "already_paid" | "already_subscribed" | "error";

const TIER_NAMES: Record<string, string> = { starter: "Starter", pro: "Pro" };

/**
 * /betaal/:code — korte, getrackte betaallink uit onze mails.
 *
 * Bij openen wordt er NIETS aangemaakt of gewijzigd: we halen alleen de stand
 * op (preview). Linkscanners van Outlook/Gmail kunnen deze pagina dus veilig
 * openen. Pas als de gebruiker op "Betaal met iDEAL" klikt maakt de edge
 * function een verse betaalsessie aan en zet ze de automatische incasso op
 * die factuur stil.
 */
const BetaalLink = () => {
  const { code } = useParams<{ code: string }>();
  const [state, setState] = useState<State>("loading");
  const [amount, setAmount] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<{ tier: string; yearly: boolean } | null>(null);
  const [paying, setPaying] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [invoiceInfo, setInvoiceInfo] = useState<{ number: string | null; url: string | null; month: string | null }>({
    number: null, url: null, month: null,
  });

  useEffect(() => {
    let cancelled = false;
    const preview = async () => {
      if (!code) {
        setState("error");
        setMessage("Deze link is niet compleet.");
        return;
      }
      const { data, error } = await supabase.functions.invoke("pay-reminder-link", {
        body: { code, action: "preview" },
      });
      if (cancelled) return;
      const result = data as {
        state?: string;
        amount_cents?: number;
        kind?: string;
        tier?: string | null;
        billing_interval?: string | null;
      } | null;
      if ((result as { error?: string } | null)?.error === "expired_or_invalid") {
        setState("error");
        setMessage("Deze betaallink is al voldaan of niet meer geldig.");
        return;
      }
      if (error || !result?.state) {
        setState("error");
        setMessage("We konden je factuur niet ophalen. Probeer het later opnieuw.");
        return;
      }
      setAmount(typeof result.amount_cents === "number" ? result.amount_cents : null);
      const r = result as { invoice_number?: string | null; invoice_period_start?: number | null; invoice_url?: string | null };
      setInvoiceInfo({
        number: r.invoice_number ?? null,
        url: r.invoice_url ?? null,
        month: r.invoice_period_start
          ? new Intl.DateTimeFormat("nl-NL", { month: "long", year: "numeric" }).format(new Date(r.invoice_period_start * 1000))
          : null,
      });
      if (result.kind === "subscription" && result.tier) {
        setSubscription({ tier: result.tier, yearly: result.billing_interval === "yearly" });
      }
      setState(
        result.state === "already_subscribed"
          ? "already_subscribed"
          : result.state === "settled"
            ? "already_paid"
            : "ready",
      );
    };
    void preview();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const startPayment = async () => {
    if (!code || paying) return;
    setPaying(true);
    const { data, error } = await supabase.functions.invoke("pay-reminder-link", {
      body: { code, action: "pay" },
    });
    const result = data as { url?: string; state?: string } | null;
    if (result?.state === "already_subscribed") {
      setPaying(false);
      setState("already_subscribed");
      return;
    }
    const target = result?.url;
    if (error || !target) {
      setPaying(false);
      setState("error");
      setMessage("We konden de betaalpagina niet openen. Probeer het later opnieuw.");
      return;
    }
    if (target.includes("betaling=al-voldaan")) {
      setPaying(false);
      setState("already_paid");
      return;
    }
    window.location.replace(target);
  };

  const bedrag =
    amount != null
      ? new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(amount / 100)
      : null;

  return (
    <>
      <SEOHead
        title="Factuur betalen | Perfecte Zorgmatch"
        description="Betaal je openstaande factuur veilig via iDEAL."
        noIndex
      />
      <main className="min-h-[70vh] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            {state === "loading" && (
              <>
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                <h1 className="text-lg font-semibold">Een moment…</h1>
                <p className="text-sm text-muted-foreground">We halen je gegevens op.</p>
              </>
            )}
            {state === "ready" && subscription && (
              <>
                <h1 className="text-lg font-semibold">
                  Start met {TIER_NAMES[subscription.tier] ?? subscription.tier}
                </h1>
                {bedrag && (
                  <p className="text-sm text-muted-foreground">
                    <strong>{bedrag}</strong> per {subscription.yearly ? "jaar" : "maand"}
                  </p>
                )}
                <Button className="w-full h-14 text-base font-bold" onClick={startPayment} disabled={paying}>
                  {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Betaal met iDEAL"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Veilig betalen met iDEAL ·{" "}
                  {subscription.yearly ? "per jaar, daarna opzegbaar" : "maandelijks opzegbaar"}
                </p>
              </>
            )}
            {state === "ready" && !subscription && (
              <>
                <h1 className="text-lg font-semibold">Betaal je verlenging met iDEAL</h1>
                {bedrag && (
                  <p className="text-sm text-muted-foreground">
                    Openstaand bedrag: <strong>{bedrag}</strong>
                  </p>
                )}
                {(invoiceInfo.month || invoiceInfo.number) && (
                  <div className="rounded-lg border px-4 py-3 text-sm text-left space-y-1">
                    {invoiceInfo.month && (
                      <p>Factuurmaand: <strong className="capitalize">{invoiceInfo.month}</strong></p>
                    )}
                    {invoiceInfo.number && <p>Factuur: <strong>{invoiceInfo.number}</strong></p>}
                    {invoiceInfo.url && (
                      <a className="underline text-primary" href={invoiceInfo.url} target="_blank" rel="noopener noreferrer">
                        Bekijk factuur
                      </a>
                    )}
                  </div>
                )}
                <Button className="w-full h-14 text-base font-bold" onClick={startPayment} disabled={paying}>
                  {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Betaal met iDEAL"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Je wordt doorgestuurd naar de beveiligde betaalomgeving van je bank.
                </p>
                <a className="text-sm underline" href="/mijn-praktijk/abonnement">Naar mijn abonnement</a>
              </>
            )}
            {state === "already_subscribed" && (
              <>
                <CheckCircle2 className="w-8 h-8 mx-auto text-primary" />
                <h1 className="text-lg font-semibold">Je hebt al een actief abonnement</h1>
                <p className="text-sm text-muted-foreground">Je hoeft niets te doen. Alles staat al voor je klaar.</p>
                <Button asChild>
                  <a href="/mijn-praktijk">Naar mijn dashboard</a>
                </Button>
              </>
            )}
            {state === "already_paid" && (
              <>
                <CheckCircle2 className="w-8 h-8 mx-auto text-primary" />
                <h1 className="text-lg font-semibold">Deze betaling is al gedaan.</h1>
                <p className="text-sm text-muted-foreground">Er staat niets meer open. Je hoeft niets te doen.</p>
                <Button asChild variant="outline">
                  <a href="/mijn-praktijk/facturatie">Naar mijn facturen</a>
                </Button>
              </>
            )}
            {state === "error" && (
              <>
                <AlertTriangle className="w-8 h-8 mx-auto text-destructive" />
                <h1 className="text-lg font-semibold">Betaalpagina niet beschikbaar</h1>
                <p className="text-sm text-muted-foreground">{message}</p>
                <p className="text-sm text-muted-foreground">
                  Neem contact op via{" "}
                  <a className="underline" href="mailto:info@perfectezorgmatch.nl">
                    info@perfectezorgmatch.nl
                  </a>{" "}
                  — we sturen je meteen een werkende betaallink.
                </p>
                <Button asChild variant="outline">
                  <a href="/mijn-praktijk/facturatie">Naar mijn facturen</a>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </>
  );
};

export default BetaalLink;
